import { KeyboardEvent, useState } from 'react';
import {
  Delete as TrashIcon,
  Search as SearchIcon,
  FileCopy as ExportIcon,
} from '@mui/icons-material';
import { SearchInput, SelectInput, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { CommerceFilters, CommerceStatus } from '../models';

interface CommercesSearchBarProps {
  initialFilter: CommerceFilters;
  filter: CommerceFilters;
  search: (filters: CommerceFilters) => void;
  onExport?: () => void;
  isExporting?: boolean;
}

type TextFilter =
  | 'name'
  | 'businessName'
  | 'externalId'
  | 'sellerId'
  | 'taxNumber'
  | 'email'
  | 'phone';

const textFilters: { field: TextFilter; placeholder: string }[] = [
  { field: 'name', placeholder: 'Buscar por nombre' },
  { field: 'businessName', placeholder: 'Buscar por razón social' },
  { field: 'externalId', placeholder: 'ID externo' },
  { field: 'sellerId', placeholder: 'Seller ID (ML)' },
  { field: 'taxNumber', placeholder: 'RUC' },
  { field: 'email', placeholder: 'Email' },
  { field: 'phone', placeholder: 'Teléfono' },
];

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: CommerceStatus.CREATED, label: 'Creado' },
  { value: CommerceStatus.ACTIVE, label: 'Activo' },
];

const CommercesSearchBar = ({
  initialFilter,
  filter,
  search,
  onExport,
  isExporting = false,
}: CommercesSearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<CommerceFilters>(filter);

  const handleStatusChange = (value: string) => {
    setLocalFilter((prev) => ({
      ...prev,
      status: value === '' ? undefined : (value as CommerceStatus),
    }));
  };

  const handleSearch = () => {
    search(localFilter);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      (event.target as HTMLElement).tagName === 'INPUT'
    ) {
      handleSearch();
    }
  };

  const resetFilters = () => {
    setLocalFilter(initialFilter);
    search(initialFilter);
  };

  return (
    <div className='w-full p-5 bg-white rounded-lg shadow-md'>
      <div className='flex flex-col gap-4'>
        <div
          className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5'
          onKeyDown={handleKeyDown}
        >
          {textFilters.map(({ field, placeholder }) => (
            <SearchInput
              key={field}
              name={`commerce-${field}-search`}
              placeholder={placeholder}
              value={localFilter[field] || ''}
              onChange={(value) =>
                setLocalFilter((prev: CommerceFilters) => ({
                  ...prev,
                  [field]: value,
                }))
              }
            />
          ))}
          <SelectInput
            name='status'
            value={localFilter.status || ''}
            onChange={handleStatusChange}
            options={statusOptions}
            placeholder='Filtrar por estado'
          />
        </div>
        <div className='flex gap-2 justify-end'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={resetFilters}
          >
            <TrashIcon />
            Limpiar
          </GlobalButton>
          {onExport && (
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={onExport}
              disabled={isExporting}
            >
              <ExportIcon />
              {isExporting ? 'Exportando…' : 'Exportar a Excel'}
            </GlobalButton>
          )}
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={handleSearch}
          >
            <SearchIcon />
            Buscar
          </GlobalButton>
        </div>
      </div>
    </div>
  );
};

export default CommercesSearchBar;
